import { useState, useRef, useLayoutEffect } from 'react';
import Card from './Card';
import { MoreVertical } from 'lucide-react';

interface ActionDropdownProps {
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ children, isOpen, onToggle }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [positionClass, setPositionClass] = useState('mr-7');
  // Effect to calculate position when dropdown opens or content changes
  useLayoutEffect(() => {
// console.log(isOpen,buttonRef,dropdownRef);
    if (isOpen && buttonRef.current && dropdownRef.current) {
      // Check for mobile screen size
      if (window.innerWidth < 768) { // Tailwind's 'md' breakpoint is 768px
        setPositionClass('bottom-full mb-2'); // Always pop up on mobile
      } else {
        // Placeholder for future positioning logic
        // const tableBody = buttonRef.current.parentNode?.parentNode?.parentNode?.parentNode;
      }
    }
  }, [isOpen]); // Re-run if isOpen or children (content) changes

  // Effect to handle clicks outside the dropdown
  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        if (isOpen) { // Only call onToggle if it's currently open
          onToggle();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation(); // Prevent the click from bubbling up to document and immediately closing the dropdown
    onToggle();
  };

  return (
    <div className="absolute flex text-left">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`p-1 hover:text-gray-700 text-sm ${isOpen ? 'bg-primary-500 text-white' : 'text-gray-500'}`}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <Card
          ref={dropdownRef}
          // Added max-h-60, overflow-y-auto, and p-2 for better mobile responsiveness
          // Updated positioning logic to prioritize opening upwards on mobile
          className={`absolute right-0 w-auto origin-top-right bg-white shadow-lg ring-1 ring-gray-200 focus:outline-none z-10 max-h-60 overflow-y-auto p-2 ${positionClass}`}
        >
          {children}
        </Card>
      )}
    </div>
  );
};

export default ActionDropdown;
